import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Gift, X } from 'lucide-react';
import CustomerLayout from '../components/layout/CustomerLayout';
import { useAuth } from '../context/AuthContext';
import type { CustomerProfile, MeResponse } from '../api/client';
import PhoneInput from '../components/form/PhoneInput';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

const DELETE_PHRASE_EXACT = 'I-want-to-delete-account';

function formatRetentionCooldownDate(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleString(locale, {
      timeZone: 'UTC',
      dateStyle: 'long',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

/** Persists the post–stay-offer success copy across a full page reload. */
const RETENTION_STAY_NOTICE_STORAGE_KEY = 'lewouhmart.account.retentionStayNotice.v1';

type DeletionFlowResponse = {
  requiredPhrase: string;
  retention: {
    enabled: boolean;
    windowDays: number;
    maxOffersPerWindow: number;
    previewsUsedThisWindow: number;
    offerTitle: string;
    offerBody: string;
    promoCode: string;
    retentionDiscountPercent: number;
    emailOfferCooldownDays?: number;
    retentionEmailCooldownUntil?: string | null;
    retentionOfferBlockedByEmailCooldown?: boolean;
    nextStepOffer: boolean;
    exitSurveyRequired: boolean;
  };
  exitReasons: Array<{ id: string; label: string }>;
};

type DeletionPreviewResponse = {
  retentionOfferBlockedByEmailCooldown?: boolean;
  retentionEmailCooldownUntil?: string | null;
  showRetentionOffer: boolean;
  retentionOffer: {
    title: string;
    body: string;
    promoCode: string | null;
    retentionDiscountPercent: number;
  } | null;
  exitSurveyRequired: boolean;
  exitReasons: Array<{ id: string; label: string }>;
  previewsUsedThisWindow: number;
  maxOffersPerWindow: number;
  windowDays: number;
};

function CustomerAccountPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    authorizedFetch,
    logout,
    registerCurrentDevicePasskey,
    regenerateRecoveryCodes,
    listPasskeys,
    renamePasskey,
    deletePasskey,
  } = useAuth();
  const [me, setMe] = useState<MeResponse>(null);
  const [profile, setProfile] = useState<CustomerProfile>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  /** Shown as a large banner after accepting the account-deletion stay offer. */
  const [retentionStayNotice, setRetentionStayNotice] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [recoveryPassword, setRecoveryPassword] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [recoveryBusy, setRecoveryBusy] = useState(false);
  const [passkeys, setPasskeys] = useState<Array<{ id: string; nickname?: string | null; createdAt: string }>>([]);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [countryInput, setCountryInput] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [addressInput, setAddressInput] = useState('');
  const [street, setStreet] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [countrySuggestions, setCountrySuggestions] = useState<Array<{ name: string; code?: string }>>([]);
  const [citySuggestions, setCitySuggestions] = useState<Array<{ name: string }>>([]);
  const [addressSuggestions, setAddressSuggestions] = useState<
    Array<{ label: string; street: string; city: string; country: string; postalCode: string; latitude?: number; longitude?: number }>
  >([]);
  const [countrySelected, setCountrySelected] = useState(false);
  const [citySelected, setCitySelected] = useState(false);
  const [addressSelected, setAddressSelected] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteLocalError, setDeleteLocalError] = useState<string | null>(null);
  const [deletionFlow, setDeletionFlow] = useState<DeletionFlowResponse | null>(null);
  const [previewResult, setPreviewResult] = useState<DeletionPreviewResponse | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [showRetentionModal, setShowRetentionModal] = useState(false);
  const [exitReasonIds, setExitReasonIds] = useState<string[]>([]);
  const [exitComment, setExitComment] = useState('');
  const [retentionBusy, setRetentionBusy] = useState(false);
  const [retentionDismissed, setRetentionDismissed] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;

    (async () => {
      try {
        const [meRes, profileRes] = await Promise.all([
          authorizedFetch(`${API_BASE}/auth/me`),
          authorizedFetch(`${API_BASE}/auth/profile`),
        ]);

        if (!meRes.ok || !profileRes.ok) {
          throw new Error(t('account.load_error'));
        }

        const meData = await meRes.json();
        const profileText = await profileRes.text();
        const profileData = profileText ? JSON.parse(profileText) : null;
        setMe(meData);
        setProfile(profileData);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : t('account.load_error'),
        );
      }
    })();
  }, [isAuthenticated, authorizedFetch, t]);

  useEffect(() => {
    if (!isAuthenticated) return;
    try {
      const raw = sessionStorage.getItem(RETENTION_STAY_NOTICE_STORAGE_KEY);
      if (!raw) return;
      sessionStorage.removeItem(RETENTION_STAY_NOTICE_STORAGE_KEY);
      const data = JSON.parse(raw) as { message?: string };
      if (typeof data.message === 'string' && data.message.trim()) {
        setSuccess(null);
        setRetentionStayNotice(data.message.trim());
      }
    } catch {
      try {
        sessionStorage.removeItem(RETENTION_STAY_NOTICE_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await authorizedFetch(`${API_BASE}/auth/account/deletion-flow`);
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as DeletionFlowResponse;
        if (!cancelled) setDeletionFlow(data);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, authorizedFetch]);

  useEffect(() => {
    if (!profile) return;
    setCountryInput(profile.country ?? '');
    setCityInput(profile.city ?? '');
    setStreet(profile.street ?? '');
    setAddressInput(profile.street ?? '');
    setPostalCode(profile.postalCode ?? '');
    setLatitude(profile.latitude ?? null);
    setLongitude(profile.longitude ?? null);
    setCountrySelected(!!profile.country);
    setCitySelected(!!profile.city);
    setAddressSelected(!!profile.street && profile.latitude != null && profile.longitude != null);
  }, [profile]);

  useEffect(() => {
    if (!isAuthenticated) return;
    void (async () => {
      const rows = await listPasskeys();
      setPasskeys(rows.map((r) => ({ id: r.id, nickname: r.nickname, createdAt: r.createdAt })));
    })();
  }, [isAuthenticated, listPasskeys]);

  useEffect(() => {
    let cancelled = false;
    const q = countryInput.trim();
    if (!isEditing || q.length < 2 || countrySelected) {
      setCountrySuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      void (async () => {
        const res = await fetch(`${API_BASE}/auth/location/countries?q=${encodeURIComponent(q)}`);
        if (!res.ok || cancelled) return;
        const rows = (await res.json()) as Array<{ name: string; code?: string }>;
        if (!cancelled) setCountrySuggestions(rows);
      })();
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [countryInput, countrySelected, isEditing]);

  useEffect(() => {
    let cancelled = false;
    const q = cityInput.trim();
    if (!isEditing || !countrySelected || q.length < 2 || citySelected) {
      setCitySuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      void (async () => {
        const url = `${API_BASE}/auth/location/cities?country=${encodeURIComponent(countryInput)}&countryCode=${encodeURIComponent(countryCode)}&q=${encodeURIComponent(q)}`;
        const res = await fetch(url);
        if (!res.ok || cancelled) return;
        const rows = (await res.json()) as Array<{ name: string }>;
        if (!cancelled) setCitySuggestions(rows);
      })();
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [cityInput, citySelected, countryCode, countryInput, countrySelected, isEditing]);

  useEffect(() => {
    let cancelled = false;
    const q = addressInput.trim();
    if (!isEditing || !countrySelected || !citySelected || q.length < 3 || addressSelected) {
      setAddressSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      void (async () => {
        const url = `${API_BASE}/auth/location/addresses?country=${encodeURIComponent(countryInput)}&countryCode=${encodeURIComponent(countryCode)}&city=${encodeURIComponent(cityInput)}&q=${encodeURIComponent(q)}`;
        const res = await fetch(url);
        if (!res.ok || cancelled) return;
        const rows = (await res.json()) as Array<{
          label: string;
          street: string;
          city: string;
          country: string;
          postalCode: string;
          latitude?: number;
          longitude?: number;
        }>;
        if (!cancelled) setAddressSuggestions(rows);
      })();
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    addressInput,
    addressSelected,
    cityInput,
    citySelected,
    countryCode,
    countryInput,
    countrySelected,
    isEditing,
  ]);

  const handleChange =
    (field: keyof NonNullable<CustomerProfile>) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setProfile((prev) =>
          prev
            ? {
                ...prev,
                [field]: e.target.value,
              }
            : {
                id: '',
                fullName: '',
                phone: '',
                street: '',
                city: '',
                postalCode: '',
                country: '',
                latitude: null,
                longitude: null,
                [field]: e.target.value,
              } as CustomerProfile,
        );
      };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    setRetentionStayNotice(null);

    try {
      if (!countryInput.trim() || !cityInput.trim() || !street.trim()) {
        throw new Error(
          t('account.save_error', { defaultValue: 'Please provide country, city, and street.' }),
        );
      }
      const addressSource: 'MANUAL' | 'SUGGESTION' =
        addressSelected && latitude != null && longitude != null
          ? 'SUGGESTION'
          : 'MANUAL';
      const res = await authorizedFetch(`${API_BASE}/auth/profile/customer`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: profile.fullName,
          phone: profile.phone,
          street,
          city: cityInput,
          postalCode: postalCode.trim() || '00000',
          country: countryInput,
          latitude,
          longitude,
          addressSource,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const message =
          typeof (body as { message?: string | string[] }).message === 'string'
            ? (body as { message?: string }).message!
            : Array.isArray((body as { message?: string[] }).message)
              ? (body as { message?: string[] }).message!.join(', ')
              : t('account.save_error');
        throw new Error(message);
      }
      const updated = await res.json();
      setProfile(updated);
      setSuccess(t('account.save_success'));
      setIsEditing(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('account.save_error'),
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) return;
    if (newPassword !== confirmPassword) {
      setError(t('account.password_mismatch'));
      return;
    }
    setSavingPassword(true);
    setError(null);
    setSuccess(null);
    setRetentionStayNotice(null);

    try {
      const res = await authorizedFetch(`${API_BASE}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const message =
          (body as { message?: string }).message
          || t('account.password_change_error');
        throw new Error(message);
      }
      setSuccess(t('account.password_change_success'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('account.password_change_error'),
      );
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSetupPasskey = async () => {
    setPasskeyBusy(true);
    setError(null);
    setSuccess(null);
    setRetentionStayNotice(null);
    try {
      await registerCurrentDevicePasskey();
      setSuccess(
        t('account.passkey_saved', {
          defaultValue: 'Passkey saved for this account.',
        }),
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('account.passkey_register_failed', {
              defaultValue: 'Failed to register passkey.',
            }),
      );
    } finally {
      setPasskeyBusy(false);
    }
    const rows = await listPasskeys();
    setPasskeys(rows.map((r) => ({ id: r.id, nickname: r.nickname, createdAt: r.createdAt })));
  };

  const handleGenerateRecoveryCodes = async (e: React.FormEvent) => {
    e.preventDefault();
    setRecoveryBusy(true);
    setError(null);
    setSuccess(null);
    setRetentionStayNotice(null);
    try {
      const codes = await regenerateRecoveryCodes(recoveryPassword);
      setRecoveryCodes(codes);
      setRecoveryPassword('');
      setSuccess(
        t('account.recovery_codes_generated', {
          defaultValue: 'New recovery codes generated. Save them in a safe place.',
        }),
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('account.recovery_codes_failed', {
              defaultValue: 'Failed to generate recovery codes.',
            }),
      );
    } finally {
      setRecoveryBusy(false);
    }
  };

  const handleRenamePasskey = async (id: string) => {
    try {
      await renamePasskey(id, renameValue.trim() || undefined);
      setPasskeys((prev) => prev.map((p) => (p.id === id ? { ...p, nickname: renameValue.trim() || null } : p)));
      setRenamingId(null);
      setRenameValue('');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('account.passkey_rename_failed', {
              defaultValue: 'Rename failed.',
            }),
      );
    }
  };

  const handleDeletePasskey = async (id: string) => {
    try {
      await deletePasskey(id);
      setPasskeys((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('account.passkey_delete_failed', {
              defaultValue: 'Delete failed.',
            }),
      );
    }
  };

  const toggleExitReason = (id: string) => {
    setExitReasonIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handlePreviewDeletion = async (e: React.FormEvent) => {
    e.preventDefault();
    setDeleteLocalError(null);
    setPreviewResult(null);
    if (deleteConfirmText.trim() !== DELETE_PHRASE_EXACT) {
      setDeleteLocalError(t('account.delete_confirm_mismatch'));
      return;
    }
    if (!deletePassword) {
      setDeleteLocalError(t('account.delete_password_required'));
      return;
    }
    setPreviewBusy(true);
    setRetentionDismissed(false);
    try {
      const res = await authorizedFetch(`${API_BASE}/auth/account/deletion-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: deletePassword,
          confirmPhrase: deleteConfirmText.trim(),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          typeof (body as { message?: string | string[] }).message === 'string'
            ? (body as { message: string }).message
            : Array.isArray((body as { message?: string[] }).message)
              ? (body as { message: string[] }).message.join(', ')
              : t('account.delete_error');
        throw new Error(msg);
      }
      const data = body as DeletionPreviewResponse;
      setPreviewResult(data);
      setExitReasonIds([]);
      setExitComment('');
      if (data.showRetentionOffer) {
        setShowRetentionModal(true);
      } else if (data.exitSurveyRequired) {
        setShowRetentionModal(false);
      }
    } catch (err) {
      setDeleteLocalError(err instanceof Error ? err.message : t('account.delete_error'));
    } finally {
      setPreviewBusy(false);
    }
  };

  const handleAcceptRetention = async () => {
    setDeleteLocalError(null);
    if (!deletePassword) {
      setDeleteLocalError(t('account.delete_password_required'));
      return;
    }
    setRetentionBusy(true);
    try {
      const res = await authorizedFetch(`${API_BASE}/auth/account/accept-retention-offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: deletePassword }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          typeof (body as { message?: string | string[] }).message === 'string'
            ? (body as { message: string }).message
            : t('account.delete_error');
        throw new Error(msg);
      }
      const accepted = body as { retentionDiscountPercent?: number };
      const pct = Number(accepted?.retentionDiscountPercent ?? 0);
      const message =
        pct > 1e-9
          ? t('account.retention_accepted_with_percent', {
              defaultValue:
                'Thanks for staying. Your next order will include a one-time {{percent}}% discount on merchandise (subtotal), shown at checkout and on your receipt.',
              percent: pct % 1 === 0 ? String(pct) : pct.toFixed(1),
            })
          : t('account.retention_accepted', {
              defaultValue:
                'Thanks for staying. If a promo code was configured, use it at checkout on your next order.',
            });
      try {
        sessionStorage.setItem(
          RETENTION_STAY_NOTICE_STORAGE_KEY,
          JSON.stringify({ message }),
        );
        window.location.reload();
      } catch {
        setShowRetentionModal(false);
        setPreviewResult(null);
        setSuccess(null);
        setRetentionStayNotice(message);
        void (async () => {
          const r = await authorizedFetch(`${API_BASE}/auth/account/deletion-flow`);
          if (r.ok) setDeletionFlow((await r.json()) as DeletionFlowResponse);
        })();
      }
    } catch (err) {
      setDeleteLocalError(err instanceof Error ? err.message : t('account.delete_error'));
    } finally {
      setRetentionBusy(false);
    }
  };

  const handleFinalDelete = async () => {
    setDeleteLocalError(null);
    if (deleteConfirmText.trim() !== DELETE_PHRASE_EXACT) {
      setDeleteLocalError(t('account.delete_confirm_mismatch'));
      return;
    }
    if (!deletePassword) {
      setDeleteLocalError(t('account.delete_password_required'));
      return;
    }
    if (previewResult?.exitSurveyRequired && exitReasonIds.length === 0) {
      setDeleteLocalError(
        t('account.delete_exit_reason_required', {
          defaultValue: 'Please select at least one reason.',
        }),
      );
      return;
    }
    setDeleteBusy(true);
    try {
      const res = await authorizedFetch(`${API_BASE}/auth/account/delete-customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: deletePassword,
          confirmPhrase: deleteConfirmText.trim(),
          exitReasonIds:
            previewResult?.exitSurveyRequired && exitReasonIds.length
              ? exitReasonIds
              : undefined,
          exitComment: exitComment.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg =
          typeof (body as { message?: string | string[] }).message === 'string'
            ? (body as { message: string }).message
            : Array.isArray((body as { message?: string[] }).message)
              ? (body as { message: string[] }).message.join(', ')
              : t('account.delete_error');
        throw new Error(msg);
      }
      logout();
      navigate('/auth', { replace: true, state: { accountDeleted: true } });
    } catch (err) {
      setDeleteLocalError(err instanceof Error ? err.message : t('account.delete_error'));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <CustomerLayout>
      <div className="flex flex-col gap-8">
        <header>
          <h1 className="workspace-page-title">
            {t('account.title')}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            {t('account.subtitle')}
          </p>
        </header>

        <section className="max-w-5xl">
          {!isAuthenticated && (
            <p className="text-sm text-slate-600">
              {t('account.sign_in_required')}
            </p>
          )}

          {isAuthenticated && retentionStayNotice ? (
            <div
              role="status"
              className="mb-8 rounded-2xl border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 via-white to-amber-50/40 px-4 py-5 shadow-lg ring-2 ring-emerald-200/90 sm:px-6 sm:py-6"
            >
              <div className="flex gap-4">
                <div
                  className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 shadow-inner sm:h-16 sm:w-16"
                  aria-hidden
                >
                  <Gift className="h-8 w-8 sm:h-9 sm:w-9" strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1 pt-0.5">
                  <p className="text-lg font-bold leading-snug text-emerald-950 sm:text-xl">
                    {t('account.retention_banner_title')}
                  </p>
                  <p className="mt-2 text-sm font-medium leading-relaxed text-emerald-950 sm:text-base">
                    {retentionStayNotice}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setRetentionStayNotice(null)}
                  className="shrink-0 self-start rounded-lg p-1.5 text-emerald-800/80 transition hover:bg-emerald-100/80 hover:text-emerald-950"
                  aria-label={t('account.retention_banner_dismiss')}
                >
                  <X className="h-5 w-5" strokeWidth={2.5} />
                </button>
              </div>
            </div>
          ) : null}

          {isAuthenticated && (
            <div className="grid gap-8 md:grid-cols-[minmax(0,2.2fr)_minmax(0,1.3fr)]">
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-slate-900">
                      {t('account.profile_section_title')}
                    </h2>
                    <p className="text-sm text-slate-500">
                      {t('account.profile_section_subtitle')}
                    </p>
                  </div>
                  {!isEditing && (
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        setSuccess(null);
                        setRetentionStayNotice(null);
                        setCountryInput(profile?.country ?? '');
                        setCountryCode('');
                        setCountrySelected(!!profile?.country);
                        setCityInput(profile?.city ?? '');
                        setCitySelected(!!profile?.city);
                        setAddressInput(profile?.street ?? '');
                        setAddressSelected(!!profile?.street && profile?.latitude != null && profile?.longitude != null);
                        setStreet(profile?.street ?? '');
                        setPostalCode(profile?.postalCode ?? '');
                        setLatitude(profile?.latitude ?? null);
                        setLongitude(profile?.longitude ?? null);
                        setIsEditing(true);
                      }}
                      className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-400"
                    >
                      {t('account.edit_button')}
                    </button>
                  )}
                </div>

                {error && (
                  <p className="mb-2 text-xs text-red-500">
                    {error}
                  </p>
                )}
                {success && (
                  <p className="mb-2 text-xs text-emerald-600">
                    {success}
                  </p>
                )}

                {isEditing ? (
                  <form className="space-y-4" onSubmit={handleSave}>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5 text-sm">
                        <label
                          className="block text-xs font-medium text-slate-700"
                          htmlFor="fullName"
                        >
                      {t('account.field_full_name')}
                        </label>
                        <input
                          id="fullName"
                          type="text"
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                          value={profile?.fullName ?? ''}
                          onChange={handleChange('fullName')}
                        />
                      </div>

                      <PhoneInput
                        id="phone"
                        label={t('account.field_phone')}
                        value={profile?.phone ?? ''}
                        onChange={(val) =>
                          setProfile((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  phone: val,
                                }
                              : {
                                  id: '',
                                  fullName: '',
                                  phone: val,
                                  street: '',
                                  city: '',
                                  postalCode: '',
                                  country: '',
                                  latitude: null,
                                  longitude: null,
                                } as CustomerProfile,
                          )
                        }
                      />
                    </div>

                    <div className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      <p className="font-semibold">
                        {t('account.strict_address_title', {
                          defaultValue: 'Strict address mode',
                        })}
                      </p>
                      <p>
                        {t('account.strict_address_hint', {
                          defaultValue:
                            'To keep delivery-time calculations reliable, select Country, City, and Address from suggestions (same pattern as signup).',
                        })}
                      </p>
                    </div>

                    <div className="space-y-1.5 text-sm">
                      <label className="block text-xs font-medium text-slate-700" htmlFor="account-country">
                        {t('account.field_country')}
                      </label>
                      <input
                        id="account-country"
                        type="text"
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                        value={countryInput}
                        onChange={(e) => {
                          setCountryInput(e.target.value);
                          setCountryCode('');
                          setCountrySelected(false);
                          setCityInput('');
                          setCitySelected(false);
                          setAddressInput('');
                          setAddressSelected(false);
                          setStreet('');
                          setPostalCode('');
                          setLatitude(null);
                          setLongitude(null);
                        }}
                        required
                      />
                      {countrySuggestions.length > 0 ? (
                        <div className="max-h-32 overflow-auto rounded-lg border border-slate-200 bg-white">
                          {countrySuggestions.map((c) => (
                            <button
                              key={c.name}
                              type="button"
                              onClick={() => {
                                setCountryInput(c.name);
                                setCountryCode(c.code || '');
                                setCountrySelected(true);
                                setCountrySuggestions([]);
                              }}
                              className="block w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
                            >
                              {c.name}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>

                    <div className="space-y-1.5 text-sm">
                      <label className="block text-xs font-medium text-slate-700" htmlFor="account-city">
                        {t('account.field_city')}
                      </label>
                      <input
                        id="account-city"
                        type="text"
                        disabled={!countrySelected}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70 disabled:cursor-not-allowed disabled:bg-slate-100"
                        value={cityInput}
                        onChange={(e) => {
                          setCityInput(e.target.value);
                          setCitySelected(false);
                          setAddressInput('');
                          setAddressSelected(false);
                          setStreet('');
                          setPostalCode('');
                          setLatitude(null);
                          setLongitude(null);
                        }}
                        required
                      />
                      {citySuggestions.length > 0 ? (
                        <div className="max-h-32 overflow-auto rounded-lg border border-slate-200 bg-white">
                          {citySuggestions.map((c) => (
                            <button
                              key={c.name}
                              type="button"
                              onClick={() => {
                                setCityInput(c.name);
                                setCitySelected(true);
                                setCitySuggestions([]);
                              }}
                              className="block w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
                            >
                              {c.name}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>

                    <div className="space-y-1.5 text-sm">
                      <label className="block text-xs font-medium text-slate-700" htmlFor="account-address">
                        {t('account.field_street')}
                      </label>
                      <input
                        id="account-address"
                        type="text"
                        disabled={!citySelected}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70 disabled:cursor-not-allowed disabled:bg-slate-100"
                        value={addressInput}
                        onChange={(e) => {
                          setAddressInput(e.target.value);
                          setAddressSelected(false);
                          setStreet('');
                          setPostalCode('');
                          setLatitude(null);
                          setLongitude(null);
                        }}
                        required
                      />
                      {addressSuggestions.length > 0 ? (
                        <div className="max-h-40 overflow-auto rounded-lg border border-slate-200 bg-white">
                          {addressSuggestions.map((a) => (
                            <button
                              key={`${a.label}-${a.latitude}-${a.longitude}`}
                              type="button"
                              onClick={() => {
                                setAddressInput(a.label);
                                setAddressSelected(true);
                                setAddressSuggestions([]);
                                setStreet(a.street || a.label);
                                setPostalCode(a.postalCode || '');
                                setCityInput(a.city || cityInput);
                                setCountryInput(a.country || countryInput);
                                setLatitude(a.latitude ?? null);
                                setLongitude(a.longitude ?? null);
                              }}
                              className="block w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
                            >
                              {a.label}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>

                    <div className="grid gap-4 sm:grid-cols-3">
                      <div className="space-y-1.5 text-sm">
                        <label className="block text-xs font-medium text-slate-700" htmlFor="account-street-locked">
                          {t('account.field_street')}
                        </label>
                        <input
                          id="account-street-locked"
                          type="text"
                          className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none"
                          value={street}
                          readOnly
                        />
                      </div>
                      <div className="space-y-1.5 text-sm">
                        <label className="block text-xs font-medium text-slate-700" htmlFor="account-postal-locked">
                          {t('account.field_postal_code')}
                        </label>
                        <input
                          id="account-postal-locked"
                          type="text"
                          className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none"
                          value={postalCode}
                          readOnly
                        />
                      </div>
                      <div className="space-y-1.5 text-sm">
                        <label className="block text-xs font-medium text-slate-700" htmlFor="account-latlng-locked">
                          {t('account.lat_lng_label', { defaultValue: 'Lat / Lng' })}
                        </label>
                        <input
                          id="account-latlng-locked"
                          type="text"
                          className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none"
                          value={
                            latitude != null && longitude != null
                              ? `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`
                              : ''
                          }
                          readOnly
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {!addressSelected ? (
                        <span className="text-[11px] text-slate-500">
                          {t('account.manual_address_verification_hint', {
                            defaultValue:
                              'Address entered manually will be verified in the background.',
                          })}
                        </span>
                      ) : null}
                      <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center justify-center rounded-md bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/80 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {saving ? t('account.saving_label') : t('account.save_button')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsEditing(false);
                          setError(null);
                          setSuccess(null);
                          setRetentionStayNotice(null);
                          setCountrySuggestions([]);
                          setCitySuggestions([]);
                          setAddressSuggestions([]);
                        }}
                        className="text-xs font-medium text-slate-500 hover:text-slate-700"
                      >
                        {t('account.cancel_button')}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4 text-sm sm:text-base">
                    <div>
                      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                        {t('account.readonly_name_label')}
                      </div>
                      <div className="mt-1 text-slate-900">
                        {profile?.fullName || t('account.not_set')}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                        {t('account.readonly_phone_label')}
                      </div>
                      <div className="mt-1 text-slate-900">
                        {profile?.phone || t('account.not_set')}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                        {t('account.readonly_address_label')}
                      </div>
                      <div className="mt-1">
                        <span
                          className={[
                            'inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                            profile?.addressVerificationStatus === 'VERIFIED'
                              ? 'bg-emerald-50 text-emerald-700'
                              : profile?.addressVerificationStatus === 'FAILED'
                                ? 'bg-rose-50 text-rose-700'
                                : 'bg-amber-50 text-amber-700',
                          ].join(' ')}
                        >
                          {profile?.addressVerificationStatus ?? 'PENDING'}
                        </span>
                      </div>
                      <div className="mt-1 text-slate-900">
                        {profile?.street || t('account.not_set')}
                      </div>
                      <div className="mt-0.5 text-slate-600">
                        {[profile?.city, profile?.postalCode, profile?.country]
                          .filter(Boolean)
                          .join(' • ') || ' '}
                      </div>
                      {profile?.addressVerificationStatus === 'FAILED' ? (
                        <div className="mt-1 text-[11px] text-rose-700">
                          {profile?.addressVerificationError ||
                            t('account.address_verification_failed_short', {
                              defaultValue:
                                'Address verification failed. Please update your address.',
                            })}
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-xs text-slate-700">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-slate-900">
                    {t('account.email_label')}
                  </span>
                  <span className="text-slate-700">
                    {me?.email}
                  </span>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-slate-900">
                    {t('account.status_label')}
                  </span>
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                    {me?.isEmailVerified ? t('account.status_verified') : t('account.status_pending')}
                  </span>
                </div>
                <div className="mt-4 border-t border-slate-200 pt-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {t('account.security_section_title')}
                    </h3>
                    {!showPasswordForm && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowPasswordForm(true);
                          setError(null);
                          setSuccess(null);
                          setRetentionStayNotice(null);
                        }}
                        className="rounded-full border border-slate-300 px-3 py-1 text-[11px] font-medium text-slate-700 hover:border-slate-400"
                      >
                        {t('account.password_open_button')}
                      </button>
                    )}
                  </div>
                  {showPasswordForm && (
                    <form className="space-y-3" onSubmit={handlePasswordChange}>
                      <div className="space-y-1.5">
                        <label
                          className="block text-[11px] font-medium text-slate-700"
                          htmlFor="accountCurrentPassword"
                        >
                          {t('account.current_password_label')}
                        </label>
                        <input
                          id="accountCurrentPassword"
                        name="current-password"
                          type="password"
                          autoComplete="current-password"
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label
                          className="block text-[11px] font-medium text-slate-700"
                          htmlFor="accountNewPassword"
                        >
                          {t('account.new_password_label')}
                        </label>
                        <input
                          id="accountNewPassword"
                        name="new-password"
                          type="password"
                          autoComplete="new-password"
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label
                          className="block text-[11px] font-medium text-slate-700"
                          htmlFor="accountConfirmPassword"
                        >
                          {t('account.confirm_password_label')}
                        </label>
                        <input
                          id="accountConfirmPassword"
                        name="new-password-confirm"
                          type="password"
                          autoComplete="new-password"
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="submit"
                          disabled={savingPassword}
                          className="inline-flex items-center justify-center rounded-lg bg-slate-900 px-3 py-2 text-[11px] font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {savingPassword
                            ? t('account.password_saving_label')
                            : t('account.password_save_button')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowPasswordForm(false);
                            setCurrentPassword('');
                            setNewPassword('');
                            setConfirmPassword('');
                          }}
                          className="text-[11px] font-medium text-slate-500 hover:text-slate-700"
                        >
                          {t('account.cancel_button')}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
                <div className="mt-4 border-t border-slate-200 pt-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t('account.passkeys_section_title', {
                      defaultValue: 'Passkeys and recovery',
                    })}
                  </h3>
                  <p className="mt-1 text-[11px] text-slate-600">
                    {t('account.passkeys_section_subtitle', {
                      defaultValue:
                        'Add a passkey to sign in without password. Generate one-time recovery codes in case you lose email access.',
                    })}
                  </p>
                  <button
                    type="button"
                    onClick={handleSetupPasskey}
                    disabled={passkeyBusy}
                    className="mt-3 inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-[11px] font-semibold text-slate-800 hover:bg-slate-100 disabled:opacity-60"
                  >
                    {passkeyBusy
                      ? t('account.passkey_setup_busy', {
                          defaultValue: 'Setting up passkey...',
                        })
                      : t('account.passkey_setup_button', {
                          defaultValue: 'Add this device as passkey',
                        })}
                  </button>
                  <form className="mt-3 space-y-2" onSubmit={handleGenerateRecoveryCodes}>
                    <input
                      type="password"
                      autoComplete="current-password"
                      placeholder={t('account.recovery_password_placeholder', {
                        defaultValue: 'Current password to generate new codes',
                      })}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-[11px] text-slate-900"
                      value={recoveryPassword}
                      onChange={(e) => setRecoveryPassword(e.target.value)}
                      required
                    />
                    <button
                      type="submit"
                      disabled={recoveryBusy}
                      className="inline-flex items-center justify-center rounded-lg bg-slate-900 px-3 py-2 text-[11px] font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                    >
                      {recoveryBusy
                        ? t('account.generating', { defaultValue: 'Generating...' })
                        : t('account.generate_recovery_codes', {
                            defaultValue: 'Generate recovery codes',
                          })}
                    </button>
                  </form>
                  {recoveryCodes.length > 0 ? (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-900">
                      <p className="mb-2 font-semibold">
                        {t('account.recovery_codes_save_once', {
                          defaultValue: 'Save these recovery codes now (shown once):',
                        })}
                      </p>
                      <div className="grid grid-cols-1 gap-1 font-mono">
                        {recoveryCodes.map((item) => (
                          <div key={item}>{item}</div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {passkeys.length > 0 ? (
                    <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3 text-[11px] text-slate-800">
                      <p className="mb-2 font-semibold">
                        {t('account.passkey_devices_title', {
                          defaultValue: 'Your passkey devices',
                        })}
                      </p>
                      <div className="space-y-2">
                        {passkeys.map((item) => (
                          <div key={item.id} className="flex flex-col gap-2 rounded border border-slate-200 p-2 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0 truncate">
                              {item.nickname ||
                                t('account.unnamed_passkey', {
                                  defaultValue: 'Unnamed passkey',
                                })}
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              {renamingId === item.id ? (
                                <>
                                  <input
                                    className="w-full min-w-0 rounded border border-slate-300 px-2 py-1 sm:w-36"
                                    value={renameValue}
                                    onChange={(e) => setRenameValue(e.target.value)}
                                  />
                                  <button type="button" onClick={() => void handleRenamePasskey(item.id)} className="text-sky-700">
                                    {t('common.save', { defaultValue: 'Save' })}
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setRenamingId(item.id);
                                    setRenameValue(item.nickname || '');
                                  }}
                                  className="text-sky-700"
                                >
                                  {t('common.rename', { defaultValue: 'Rename' })}
                                </button>
                              )}
                              <button type="button" onClick={() => void handleDeletePasskey(item.id)} className="text-red-700">
                                {t('common.delete', { defaultValue: 'Delete' })}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="mt-2 rounded-2xl border border-rose-200 bg-rose-50/50 p-4 sm:p-6 md:col-span-2">
                <h2 className="text-base font-semibold text-rose-900">
                  {t('account.delete_section_title')}
                </h2>
                <p className="mt-2 text-sm text-rose-900/90">
                  {t('account.delete_section_intro')}
                </p>
                <p className="mt-2 text-xs text-rose-900/85">
                  {t('account.delete_email_retention')}
                </p>
                {deletionFlow?.retention?.retentionOfferBlockedByEmailCooldown &&
                deletionFlow.retention.retentionEmailCooldownUntil ? (
                  <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                    {t('account.retention_email_cooldown_notice', {
                      date: formatRetentionCooldownDate(
                        deletionFlow.retention.retentionEmailCooldownUntil,
                        i18n.language,
                      ),
                    })}
                  </p>
                ) : null}
                <p className="mt-2 text-xs font-medium text-rose-900">
                  <span className="align-middle">{t('account.delete_phrase_hint_before')}</span>{' '}
                  <span
                    className="notranslate align-middle inline-flex max-w-full items-center break-all rounded-md border border-rose-300 bg-white px-2 py-1 font-mono text-[11px] font-semibold leading-snug tracking-tight text-rose-950 shadow-sm ring-1 ring-rose-100"
                  >
                    {DELETE_PHRASE_EXACT}
                  </span>
                </p>
                <form className="mt-4 max-w-md space-y-3" onSubmit={handlePreviewDeletion}>
                  {deleteLocalError ? (
                    <p className="text-xs text-red-600">{deleteLocalError}</p>
                  ) : null}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-medium text-rose-900" htmlFor="deleteAccountPassword">
                      {t('account.delete_password_label')}
                    </label>
                    <input
                      id="deleteAccountPassword"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      className="w-full rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-200"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-xs font-medium text-rose-900" htmlFor="deleteAccountConfirm">
                      {t('account.delete_confirm_label')}
                    </label>
                    <input
                      id="deleteAccountConfirm"
                      type="text"
                      autoComplete="off"
                      className="w-full rounded-lg border border-rose-200 bg-white px-3 py-2 font-mono text-sm text-slate-900 outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-200"
                      placeholder={DELETE_PHRASE_EXACT}
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={previewBusy}
                    className="inline-flex items-center justify-center rounded-lg bg-rose-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {previewBusy
                      ? t('account.delete_preview_busy', { defaultValue: 'Checking…' })
                      : t('account.delete_preview_submit', { defaultValue: 'Continue' })}
                  </button>
                </form>

                {previewResult?.retentionOfferBlockedByEmailCooldown &&
                previewResult.retentionEmailCooldownUntil ? (
                  <p className="mt-4 max-w-lg rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                    {t('account.retention_email_cooldown_notice', {
                      date: formatRetentionCooldownDate(
                        previewResult.retentionEmailCooldownUntil,
                        i18n.language,
                      ),
                    })}
                  </p>
                ) : null}

                {previewResult &&
                previewResult.exitSurveyRequired &&
                (!previewResult.showRetentionOffer || retentionDismissed) &&
                !showRetentionModal ? (
                  <div className="mt-6 max-w-lg rounded-xl border border-rose-200 bg-white/80 p-4">
                    <p className="text-sm font-semibold text-rose-900">
                      {t('account.delete_exit_title', {
                        defaultValue: 'Before we remove your account, tell us why you are leaving',
                      })}
                    </p>
                    <div className="mt-3 grid gap-2">
                      {(previewResult.exitReasons ?? []).map((r) => (
                        <label
                          key={r.id}
                          className="flex cursor-pointer items-start gap-2 text-sm text-slate-800"
                        >
                          <input
                            type="checkbox"
                            checked={exitReasonIds.includes(r.id)}
                            onChange={() => toggleExitReason(r.id)}
                            className="mt-0.5"
                          />
                          <span>{r.label}</span>
                        </label>
                      ))}
                    </div>
                    <label className="mt-3 block text-xs font-medium text-slate-700">
                      {t('account.delete_exit_comment', { defaultValue: 'Optional comment' })}
                      <textarea
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        rows={3}
                        value={exitComment}
                        onChange={(e) => setExitComment(e.target.value)}
                      />
                    </label>
                    <button
                      type="button"
                      className="mt-4 inline-flex rounded-lg bg-rose-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-rose-800 disabled:opacity-50"
                      disabled={deleteBusy}
                      onClick={() => void handleFinalDelete()}
                    >
                      {deleteBusy ? t('account.delete_submitting') : t('account.delete_submit')}
                    </button>
                  </div>
                ) : null}

                {previewResult &&
                !previewResult.exitSurveyRequired &&
                (!previewResult.showRetentionOffer || retentionDismissed) &&
                !showRetentionModal ? (
                  <div className="mt-6 max-w-md">
                    <p className="text-sm text-rose-900">
                      {t('account.delete_final_confirm', {
                        defaultValue: 'You can delete your account permanently now.',
                      })}
                    </p>
                    <button
                      type="button"
                      className="mt-3 inline-flex rounded-lg bg-rose-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-rose-800 disabled:opacity-50"
                      disabled={deleteBusy}
                      onClick={() => void handleFinalDelete()}
                    >
                      {deleteBusy ? t('account.delete_submitting') : t('account.delete_submit')}
                    </button>
                  </div>
                ) : null}

                {showRetentionModal && previewResult?.retentionOffer
                  ? createPortal(
                      <div
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4"
                        role="dialog"
                        aria-modal
                        aria-labelledby="retention-title"
                      >
                        <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
                          <h3 id="retention-title" className="text-lg font-bold text-slate-900">
                            {previewResult.retentionOffer.title}
                          </h3>
                          <p className="mt-3 whitespace-pre-wrap text-sm text-slate-600">
                            {previewResult.retentionOffer.body}
                          </p>
                          {previewResult.retentionOffer.retentionDiscountPercent > 1e-9 ? (
                            <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50/90 px-3 py-2.5 text-sm font-medium text-amber-950">
                              {t('account.retention_offer_percent', {
                                defaultValue:
                                  'If you stay: {{percent}}% off your next order’s merchandise subtotal (one time, applied at checkout).',
                                percent:
                                  previewResult.retentionOffer.retentionDiscountPercent % 1 === 0
                                    ? String(previewResult.retentionOffer.retentionDiscountPercent)
                                    : previewResult.retentionOffer.retentionDiscountPercent.toFixed(1),
                              })}
                            </p>
                          ) : null}
                          {previewResult.retentionOffer.promoCode ? (
                            <p className="mt-4 rounded-lg bg-emerald-50 px-3 py-2 font-mono text-sm font-bold text-emerald-900">
                              {t('account.retention_code', {
                                defaultValue: 'Code: {{code}}',
                                code: previewResult.retentionOffer.promoCode,
                              })}
                            </p>
                          ) : null}
                          <div className="mt-6 flex flex-col gap-3">
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                disabled={retentionBusy}
                                className="rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                                onClick={() => void handleAcceptRetention()}
                              >
                                {retentionBusy
                                  ? t('common.loading', { defaultValue: 'Loading…' })
                                  : t('account.retention_stay', { defaultValue: 'Stay with this offer' })}
                              </button>
                              <button
                                type="button"
                                className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                                onClick={() => {
                                  setRetentionDismissed(true);
                                  setShowRetentionModal(false);
                                }}
                              >
                                {t('account.retention_leave', { defaultValue: 'Delete my account anyway' })}
                              </button>
                            </div>
                            <button
                              type="button"
                              className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                              onClick={() => setShowRetentionModal(false)}
                            >
                              {t('account.retention_cancel')}
                            </button>
                          </div>
                        </div>
                      </div>,
                      document.body,
                    )
                  : null}

              </div>
            </div>
          )}
        </section>
      </div>
    </CustomerLayout>
  );
}

export default CustomerAccountPage;

